import { MeasurementSystem } from '../types/MeasurementSystem';
import { ConvertArgs, UnitConverter } from './types';

const TemperatureUnits = ['K', 'C', 'F'] as const;
type TTemperatureUnit = (typeof TemperatureUnits)[number];
type TTemperatureUnitMetric = 'C';
type TTemperatureUnitImperial = 'F';

type ToBaseArgs = Parameters<UnitConverter<TTemperatureUnit, 'K'>['toBase']>[0];

type TemperatureUnitForSystem<MSystem extends MeasurementSystem> = MSystem extends MeasurementSystem.METRIC
    ? TTemperatureUnitMetric
    : MSystem extends MeasurementSystem.IMPERIAL
      ? TTemperatureUnitImperial
      : never;

export class TemperatureConverter<MSystem extends MeasurementSystem> extends UnitConverter<TTemperatureUnit, 'K'> {
    baseUnit = 'K' as const;
    units = TemperatureUnits;
    system: MSystem;

    constructor(system: MSystem) {
        super();

        this.system = system;
    }

    convert<ToUnit extends TTemperatureUnit>({ value, from, to }: ConvertArgs<TTemperatureUnit, ToUnit>) {
        const { value: baseValue } = this.toBase({ value, from });

        return {
            value: this.getConvertedValue(baseValue, to),
            unit: to,
        };
    }

    private getConvertedValue(value: number, unit: TTemperatureUnit) {
        switch (unit) {
            case 'C':
                return kToC(value);
            case 'F':
                return kToF(value);
            case 'K':
                return value;
        }
    }

    toBase({ value, from }: ToBaseArgs) {
        return {
            value: this.getBaseValue(value, from),
            unit: this.baseUnit,
        };
    }

    private getBaseValue(value: number, unit: TTemperatureUnit) {
        switch (unit) {
            case 'C':
                return cToK(value);
            case 'F':
                return fToK(value);
            case 'K':
                return value;
        }
    }

    getDisplayUnits(): TTemperatureUnit[] {
        return ['C', 'F'];
    }

    protected getAvailableBestUnits(): TemperatureUnitForSystem<MSystem>[] {
        switch (this.system) {
            case MeasurementSystem.METRIC:
                return ['C'] as TemperatureUnitForSystem<MSystem>[];
            case MeasurementSystem.IMPERIAL:
                return ['F'] as TemperatureUnitForSystem<MSystem>[];

            default:
                throw new Error('Invalid measurement system');
        }
    }
}

const cToF = (c: number) => c * 1.8 + 32;
const fToC = (f: number) => (f - 32) / 1.8;
const kToC = (k: number) => k - 273.15;
const cToK = (c: number) => c + 273.15;
const kToF = (k: number) => cToF(kToC(k));
const fToK = (f: number) => cToK(fToC(f));
